import { auth, db } from "../../configs/firebase"
import { createContext, useContext, useEffect, useState } from "react"
import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore"
import { onAuthStateChanged, signOut, updateProfile } from "firebase/auth"

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userRef = doc(db, "users", firebaseUser.uid)

        // Ensure user doc exists and update last login
        const userSnap = await getDoc(userRef)
        if (!userSnap.exists()) {
          await setDoc(userRef, {
            email: firebaseUser.email,
            displayName: firebaseUser.displayName || "",
            avatarUrl: firebaseUser.photoURL || "",
            _id: firebaseUser.uid,
            _createdAt: serverTimestamp(),
            _lastLogin: serverTimestamp(),
          })
        } else {
          await setDoc(
            userRef,
            { _lastLogin: serverTimestamp() },
            { merge: true }
          )
        }

        // Subscribe to Firestore user doc to keep context in sync
        const unsubscribeDoc = onSnapshot(userRef, (snap) => {
          const data = snap.data() || {}
          const merged = {
            ...firebaseUser,
            // Prefer Firestore values when present
            displayName:
              typeof data.displayName === "string" && data.displayName !== ""
                ? data.displayName
                : firebaseUser.displayName || "",
            avatarUrl:
              typeof data.avatarUrl === "string" && data.avatarUrl !== ""
                ? data.avatarUrl
                : firebaseUser.photoURL || "",
            photoURL:
              typeof data.avatarUrl === "string" && data.avatarUrl !== ""
                ? data.avatarUrl
                : firebaseUser.photoURL || "",
            email: firebaseUser.email,
            uid: firebaseUser.uid,
          }
          setUser(merged)
          setLoading(false)
        })

        // Return Firestore unsubscribe for this auth session
        return () => unsubscribeDoc()
      } else {
        setUser(null)
        setLoading(false)
      }
    })

    return () => unsubscribeAuth()
  }, [])

  const logout = () => signOut(auth)

  const updateUserProfile = async ({ displayName, avatarUrl }) => {
    if (!user) return
    const userRef = doc(db, "users", user.uid)
    const payload = {}
    if (typeof displayName === "string") payload.displayName = displayName
    if (typeof avatarUrl === "string") payload.avatarUrl = avatarUrl

    await updateDoc(userRef, payload)
    // Also sync to Firebase Auth profile for consistency across SDK consumers
    try {
      await updateProfile(auth.currentUser, {
        displayName: typeof displayName === "string" ? displayName : undefined,
        photoURL: typeof avatarUrl === "string" ? avatarUrl : undefined,
      })
    } catch (e) {
      console.warn("Failed to update Firebase Auth profile (non-blocking)", e)
    }
    // Local optimistic update; onSnapshot will reconcile
    setUser((prev) => ({
      ...prev,
      displayName:
        typeof displayName === "string" ? displayName : prev?.displayName,
      photoURL: typeof avatarUrl === "string" ? avatarUrl : prev?.photoURL,
      avatarUrl: typeof avatarUrl === "string" ? avatarUrl : prev?.avatarUrl,
    }))
  }

  const deleteAccount = async () => {
    try {
      const current = auth.currentUser
      if (!current || !user?.uid) {
        throw new Error("Not authenticated")
      }

      const idToken = await current.getIdToken(true)
      if (!idToken) {
        throw new Error("Failed to get authentication token")
      }

      const response = await fetch(
        "https://us-central1-aigree-5d197.cloudfunctions.net/deleteAccount",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ uid: user.uid }),
        }
      )

      let result
      try {
        result = await response.json()
      } catch {
        result = {}
      }

      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || `Delete failed (${response.status})`)
      }

      await logout()
    } catch (error) {
      console.error("Delete account error:", error)
      throw error
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        logout,
        updateUserProfile,
        isAuthenticated: !!user,
        deleteAccount,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
